// src/features/usage/hooks/useUsage.ts
import { useQuery } from '@tanstack/react-query';
import {
  fetchUsageSummary,
  fetchDeviceUsage,
  fetchTimeseries,
  fetchLedger,
  type UsageSummary,
  type DeviceUsageResponse,
  type TimeseriesResponse,
  type LedgerResponse,
  OP_WEIGHTS,
  type OpType,
} from '../api/usageApi';

export function useUsageSummary() {
  return useQuery<UsageSummary, Error>({
    queryKey:  ['usage', 'summary'],
    queryFn:   fetchUsageSummary,
    staleTime: 30_000,
  });
}

export function useDeviceUsage(params?: Record<string, string>) {
  return useQuery<DeviceUsageResponse, Error>({
    queryKey:  ['usage', 'devices', params],
    queryFn:   () => fetchDeviceUsage(params),
    staleTime: 30_000,
  });
}

export function useTimeseries(params?: Record<string, string>) {
  return useQuery<TimeseriesResponse, Error>({
    queryKey:  ['usage', 'timeseries', params],
    queryFn:   () => fetchTimeseries(params),
    staleTime: 60_000,
  });
}

export function useLedger(params?: Record<string, string>) {
  return useQuery<LedgerResponse, Error>({
    queryKey:  ['usage', 'ledger', params],
    queryFn:   () => fetchLedger(params),
    staleTime: 30_000,
  });
}

// ── P16 Credit Telemetry — derived credit badge state ───────────────────

export type CreditLevel = 'ok' | 'warning' | 'critical' | 'exceeded';

export interface CreditBadgeState {
  level:             CreditLevel;
  balance:           number;
  todayPctHardLimit: number;
  isMeteringActive:  boolean;
  isLoading:         boolean;
}

/**
 * Derives a credit badge state from the usage summary.
 * Reuses `useUsageSummary` — does NOT make an extra network call.
 */
export function useCreditBadge(): CreditBadgeState {
  const { data, isLoading } = useUsageSummary();

  if (isLoading || !data) {
    return { level: 'ok', balance: 0, todayPctHardLimit: 0, isMeteringActive: false, isLoading };
  }

  const pct = data.todayPctHardLimit ?? 0;
  let level: CreditLevel = 'ok';
  if (pct >= 100) level = 'exceeded';
  else if (pct >= 80)  level = 'critical';
  else if (pct >= 50)  level = 'warning';

  return {
    level,
    balance:           data.creditBalance,
    todayPctHardLimit: pct,
    isMeteringActive:  data.isMeteringActive,
    isLoading,
  };
}

/**
 * Estimate credit cost for an operation before executing it.
 */
export function estimateCost(opType: OpType, rowCount = 1): number {
  return (OP_WEIGHTS[opType] ?? 1) * rowCount;
}
